<template>
	<div class="email-block"
		:class="{ 'email-block--selected': selected, 'email-block--hover': hovering, 'email-block--layout': isLayout, 'email-block--show-borders': showBorders }"
		@click.stop="$emit('select', block.id)"
		@mouseenter="hovering = true"
		@mouseleave="hovering = false">

		<!-- Block type badge -->
		<div v-show="hovering || selected" class="email-block__badge">
			{{ blockLabel }}
		</div>

		<!-- Block toolbar -->
		<div v-show="hovering || selected" class="email-block__toolbar">
			<button type="button" class="email-block__tool email-block__tool--ai" title="Chiedi all'assistente AI su questo blocco" @click.stop="$emit('ai', block.id)">
				<v-icon small>mdi-robot-outline</v-icon>
			</button>
			<button type="button" class="email-block__tool" title="Sposta su" :disabled="isFirst" @click.stop="$emit('move-up', block.id)">
				<v-icon small>mdi-arrow-up</v-icon>
			</button>
			<button type="button" class="email-block__tool" title="Sposta giù" :disabled="isLast" @click.stop="$emit('move-down', block.id)">
				<v-icon small>mdi-arrow-down</v-icon>
			</button>
			<button type="button" class="email-block__tool" title="Duplica" @click.stop="$emit('duplicate', block.id)">
				<v-icon small>mdi-content-copy</v-icon>
			</button>
			<button type="button" class="email-block__tool email-block__tool--danger" title="Elimina" @click.stop="$emit('delete', block.id)">
				<v-icon small>mdi-delete</v-icon>
			</button>
		</div>

		<!-- Layout block with columns -->
		<div v-if="isLayout" class="email-block__columns" :style="blockStyles">
			<div v-for="(col, ci) in block.columns" :key="ci"
				class="email-block__column"
				:style="{ width: col.width, minHeight: '60px' }">
				<draggable
					:list="col.children"
					:group="{ name: 'blocks', put: true, pull: true }"
					ghost-class="email-block__ghost"
					:animation="200"
					class="email-block__column-drop"
					@change="onChildChange">

					<!-- Il riquadro che dice "trascina qui" deve STARE DENTRO la zona di rilascio:
					     fuori, era l'unico punto della colonna dove il rilascio non funzionava (il
					     blocco finiva in cima al documento) e raddoppiava l'altezza della colonna vuota -->
					<div v-if="!col.children.length" slot="header" class="email-block__column-placeholder">
						<v-icon x-small color="grey lighten-1">mdi-plus</v-icon>
						<span class="caption grey--text">Trascina qui</span>
					</div>

					<email-block
						v-for="(child, chi) in col.children"
						:key="child.id"
						:block="child"
						:selected="selectedBlockId === child.id"
						:is-first="chi === 0"
						:is-last="chi === col.children.length - 1"
						:selected-block-id="selectedBlockId"
						:show-borders="showBorders"
						:template-variables="templateVariables"
						@select="$emit('select', $event)"
						@move-up="moveChildUp(col, chi)"
						@move-down="moveChildDown(col, chi)"
						@duplicate="duplicateChild(col, chi)"
						@delete="deleteChild(col, chi)"
						@ai="$emit('ai', $event)"
						@content-change="$emit('content-change', $event)"
						@settings-change="$emit('settings-change', $event)"
						@child-change="$emit('child-change')">
					</email-block>
				</draggable>
			</div>
		</div>

		<!-- Content block -->
		<div v-else class="email-block__content-wrapper">
			<!-- Inline text toolbar -->
			<div v-if="editing" class="email-block__text-toolbar" @mousedown.prevent>
				<v-btn icon @click="execCmd('bold')" :class="{ 'email-block__tb-active': isBold }" class="email-block__tb-btn">
					<v-icon size="18">mdi-format-bold</v-icon>
				</v-btn>
				<v-btn icon @click="execCmd('italic')" :class="{ 'email-block__tb-active': isItalic }" class="email-block__tb-btn">
					<v-icon size="18">mdi-format-italic</v-icon>
				</v-btn>
				<v-btn icon @click="execCmd('underline')" :class="{ 'email-block__tb-active': isUnderline }" class="email-block__tb-btn">
					<v-icon size="18">mdi-format-underline</v-icon>
				</v-btn>
				<v-btn icon @click="execCmd('strikeThrough')" class="email-block__tb-btn">
					<v-icon size="18">mdi-format-strikethrough</v-icon>
				</v-btn>
				<v-divider vertical class="mx-1"></v-divider>
				<v-btn icon @click="toggleLinkInput" :class="{ 'email-block__tb-active': showLinkInput }" class="email-block__tb-btn">
					<v-icon size="18">mdi-link</v-icon>
				</v-btn>
				<template v-if="templateVariables.length">
					<v-divider vertical class="mx-1"></v-divider>
					<!-- Niente saveSelection all'apertura: Vuetify da' il focus all'attivatore e il
					     cursore e' gia' tornato in testa al blocco. Vale quello salvato su keyup/mouseup -->
					<v-menu offset-y :close-on-content-click="true">
						<template v-slot:activator="{ on }">
							<v-btn small text v-on="on" class="email-block__tb-var-btn">
								<v-icon size="18" left>mdi-code-braces</v-icon>
								Variabili
							</v-btn>
						</template>
						<v-list dense>
							<v-list-item v-for="(v, vi) in templateVariables" :key="vi"
								@click="insertVariable(v)">
								<v-list-item-content>
									<v-list-item-title class="body-2">{{ v.text || v }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
							<v-divider></v-divider>
							<v-subheader>Link</v-subheader>
							<v-list-item v-for="v in linkVariables" :key="v.name"
								@click="insertLinkVariable(v)">
								<v-list-item-content>
									<v-list-item-title class="body-2">{{ v.name }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-list>
					</v-menu>
				</template>
			</div>

			<!-- Link input inline (sotto la toolbar) -->
			<div v-if="showLinkInput" class="email-block__link-bar" @mousedown="onLinkBarMousedown">
				<v-icon size="14" color="#888" class="mr-1 flex-shrink-0">mdi-link</v-icon>
				<input
					ref="linkInput"
					v-model="linkUrl"
					type="text"
					placeholder="https://esempio.com"
					class="email-block__link-field"
					@keydown.enter="confirmLink"
					@keydown.esc="closeLinkInput">
				<v-btn x-small text color="primary" @click="confirmLink" :disabled="!linkUrl.trim()" class="email-block__link-action">
					<v-icon x-small left>{{ editingExistingLink ? 'mdi-check' : 'mdi-plus' }}</v-icon>
					{{ editingExistingLink ? 'Aggiorna' : 'Applica' }}
				</v-btn>
				<v-btn v-if="editingExistingLink" x-small text color="error" @click="removeLink" class="email-block__link-action">
					<v-icon x-small left>mdi-link-off</v-icon>
					Rimuovi
				</v-btn>
				<v-btn x-small icon @click="closeLinkInput" class="email-block__link-close">
					<v-icon size="14">mdi-close</v-icon>
				</v-btn>
			</div>

			<!-- click.prevent: nel canvas i link non devono navigare (aprirebbero l'URL in una tab nuova) -->
			<div class="email-block__content"
				:style="blockStyles"
				ref="contentEl"
				v-html="block.content"
				@click.prevent
				@dblclick="onDoubleClick"
				@blur="onBlur"
				@keyup="updateToolbarState"
				@mouseup="updateToolbarState">
			</div>

			<!-- Resize handles per immagini -->
			<template v-if="selected && block.type === 'image'">
				<div class="email-block__resize email-block__resize--e" @mousedown.stop.prevent="startResize($event, 'e')"></div>
				<div class="email-block__resize email-block__resize--w" @mousedown.stop.prevent="startResize($event, 'w')"></div>
				<div class="email-block__resize email-block__resize--se" @mousedown.stop.prevent="startResize($event, 'se')"></div>
				<div class="email-block__resize email-block__resize--sw" @mousedown.stop.prevent="startResize($event, 'sw')"></div>
			</template>
		</div>
	</div>
</template>

<script>
import draggable from 'vuedraggable'
import { findBlockDef, cloneBlockDeep } from './emailBlocks'

// All'invio diventano un URL (MailMarketingManager): vanno in un href, come testo il
// destinatario leggerebbe l'indirizzo nudo. Fuori da templateVariables perche' quella
// lista arriva anche all'assistente, che la tratta come dati del destinatario
var LINK_VARIABLES = [
	{ name: 'UNSUBSCRIBED', label: 'Disiscriviti' },
	{ name: 'TRACKING_PREFERENCES', label: 'Preferenze di tracciamento' }
]

export default {
	name: 'email-block',

	components: { draggable },

	props: {
		block: { type: Object, required: true },
		selected: { type: Boolean, default: false },
		isFirst: { type: Boolean, default: false },
		isLast: { type: Boolean, default: false },
		selectedBlockId: { type: String, default: null },
		showBorders: { type: Boolean, default: false },
		templateVariables: { type: Array, default: function() { return [] } }
	},

	data() {
		return {
			hovering: false,
			editing: false,
			isBold: false,
			isItalic: false,
			isUnderline: false,
			savedRange: null,
			showLinkInput: false,
			linkUrl: '',
			editingExistingLink: false,
			linkVariables: LINK_VARIABLES
		}
	},

	computed: {
		blockLabel() {
			var def = findBlockDef(this.block.type)
			return def ? def.label : this.block.type
		},

		blockStyles() {
			return this.block.styles || {}
		},

		isLayout() {
			return !!this.block.columns
		}
	},

	methods: {
		onDoubleClick(e) {
			if (this.isLayout) return
			if (this.block.editable) {
				// Testo libero: tutto il blocco e' editabile
				this.editing = true
				var el = this.$refs.contentEl
				el.contentEditable = 'true'
				el.focus()
				return
			}
			// Blocchi a settings: si edita sul posto solo l'elemento marcato data-field,
			// e il valore torna nei settings (il content viene poi rigenerato dal renderer)
			var target = e && e.target && e.target.closest ? e.target.closest('[data-field]') : null
			if (!target || !this.$refs.contentEl.contains(target)) return
			this.startFieldEdit(target)
		},

		startFieldEdit(el) {
			var self = this
			var field = el.getAttribute('data-field')
			el.contentEditable = 'true'
			el.classList.add('email-block__field--editing')
			el.focus()
			document.execCommand('selectAll', false, null)

			function onKeydown(ev) {
				if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); el.blur() }
				if (ev.key === 'Escape') { ev.preventDefault(); el.blur() }
			}
			function onBlur() {
				el.removeEventListener('blur', onBlur)
				el.removeEventListener('keydown', onKeydown)
				el.contentEditable = 'false'
				el.classList.remove('email-block__field--editing')
				var value = el.innerText.replace(/\u00a0/g, ' ').replace(/\s+$/, '')
				var settings = JSON.parse(JSON.stringify(self.block.settings || {}))
				self.setPath(settings, field, value)
				self.$emit('settings-change', { id: self.block.id, settings: settings })
			}
			el.addEventListener('blur', onBlur)
			el.addEventListener('keydown', onKeydown)
		},

		// 'items.2.name' → settings.items[2].name = value
		setPath(obj, path, value) {
			var parts = path.split('.')
			var cur = obj
			for (var i = 0; i < parts.length - 1; i++) {
				if (cur[parts[i]] == null) cur[parts[i]] = /^\d+$/.test(parts[i + 1]) ? [] : {}
				cur = cur[parts[i]]
			}
			cur[parts[parts.length - 1]] = value
		},

		onBlur() {
			if (!this.editing) return
			var self = this
			setTimeout(function() {
				// Non chiudere se il focus e' ancora dentro il blocco o su un menu Vuetify
				var active = document.activeElement
				if (self.$el.contains(active)) return
				if (document.querySelector('.v-menu__content:hover')) return
				if (document.querySelector('.menuable__content__active')) return

				self.editing = false
				var el = self.$refs.contentEl
				if (el) {
					el.contentEditable = 'false'
					self.$emit('content-change', { id: self.block.id, content: el.innerHTML })
				}
			}, 200)
		},

		saveSelection: function() {
			var sel = window.getSelection()
			if (sel && sel.rangeCount > 0) {
				this.savedRange = sel.getRangeAt(0).cloneRange()
			}
		},

		restoreSelection: function() {
			if (!this.savedRange) return
			var sel = window.getSelection()
			sel.removeAllRanges()
			sel.addRange(this.savedRange)
		},

		execCmd: function(cmd, value) {
			this.restoreSelection()
			document.execCommand(cmd, false, value || null)
			this.saveSelection()
			this.updateToolbarState()
		},

		insertVariable: function(variable) {
			var val = variable.value || variable
			if (val.indexOf('{{') !== 0) {
				val = '{{' + val + '}}'
			}
			this.restoreSelection()
			document.execCommand('insertHTML', false, val)
			this.saveSelection()
		},

		// Il testo selezionato diventa il link; senza selezione si scrive l'etichetta e si
		// seleziona. Non insertHTML: Chrome copierebbe sul link il font della pagina
		insertLinkVariable: function(variable) {
			this.restoreSelection()
			var sel = window.getSelection()
			if (sel.isCollapsed) {
				document.execCommand('insertText', false, variable.label)
				sel.setBaseAndExtent(sel.focusNode, sel.focusOffset - variable.label.length, sel.focusNode, sel.focusOffset)
			}
			document.execCommand('createLink', false, '{{' + variable.name + '}}')
			this.styleLinks()
		},

		toggleLinkInput: function() {
			if (this.showLinkInput && !this.editingExistingLink) {
				this.closeLinkInput()
				return
			}
			this.saveSelection()
			var link = this.getParentLink()
			if (link) {
				this.linkUrl = link.getAttribute('href') || ''
				this.editingExistingLink = true
			} else {
				this.linkUrl = ''
				this.editingExistingLink = false
			}
			this.showLinkInput = true
			var self = this
			this.$nextTick(function() {
				if (self.$refs.linkInput) self.$refs.linkInput.focus()
			})
		},

		confirmLink: function() {
			if (!this.linkUrl.trim()) return
			var url = this.linkUrl.trim()
			this.showLinkInput = false
			this.restoreSelection()
			document.execCommand('createLink', false, url)
			this.styleLinks()
			this.linkUrl = ''
		},

		// Applica stile inline a tutti i link nel blocco
		styleLinks: function() {
			var el = this.$refs.contentEl
			if (!el) return
			var links = el.querySelectorAll('a')
			for (var i = 0; i < links.length; i++) {
				links[i].style.color = '#1976d2'
				links[i].style.textDecoration = 'underline'
				links[i].setAttribute('target', '_blank')
			}
			this.$emit('content-change', { id: this.block.id, content: el.innerHTML })
			el.focus()
		},

		updateToolbarState: function() {
			this.saveSelection()
			this.isBold = document.queryCommandState('bold')
			this.isItalic = document.queryCommandState('italic')
			this.isUnderline = document.queryCommandState('underline')

			// Rileva se il cursore e' dentro un <a>
			var link = this.getParentLink()
			if (link) {
				this.linkUrl = link.getAttribute('href') || ''
				this.editingExistingLink = true
				this.showLinkInput = true
			} else if (this.editingExistingLink) {
				// Era su un link, ora non piu' — chiudi
				this.showLinkInput = false
				this.editingExistingLink = false
				this.linkUrl = ''
			}
		},

		getParentLink: function() {
			var sel = window.getSelection()
			if (!sel || sel.rangeCount === 0) return null
			var node = sel.anchorNode
			while (node && node !== this.$refs.contentEl) {
				if (node.nodeType === 1 && node.tagName === 'A') return node
				node = node.parentNode
			}
			return null
		},

		onLinkBarMousedown: function(e) {
			// Permetti focus sull'input, previeni su tutto il resto
			if (e.target.tagName === 'INPUT') return
			e.preventDefault()
		},

		closeLinkInput: function() {
			this.showLinkInput = false
			this.editingExistingLink = false
			this.linkUrl = ''
		},

		removeLink: function() {
			this.restoreSelection()
			var link = this.getParentLink()
			if (link) {
				// Seleziona tutto il contenuto del link
				var range = document.createRange()
				range.selectNodeContents(link)
				var sel = window.getSelection()
				sel.removeAllRanges()
				sel.addRange(range)
			}
			document.execCommand('unlink', false, null)
			this.showLinkInput = false
			this.editingExistingLink = false
			this.linkUrl = ''
			var el = this.$refs.contentEl
			if (el) {
				this.$emit('content-change', { id: this.block.id, content: el.innerHTML })
				el.focus()
			}
		},

		onChildChange() {
			this.$emit('child-change')
		},

		moveChildUp(col, index) {
			if (index <= 0) return
			var temp = col.children[index]
			this.$set(col.children, index, col.children[index - 1])
			this.$set(col.children, index - 1, temp)
			this.$emit('child-change')
		},

		moveChildDown(col, index) {
			if (index >= col.children.length - 1) return
			var temp = col.children[index]
			this.$set(col.children, index, col.children[index + 1])
			this.$set(col.children, index + 1, temp)
			this.$emit('child-change')
		},

		duplicateChild(col, index) {
			col.children.splice(index + 1, 0, cloneBlockDeep(col.children[index]))
			this.$emit('child-change')
		},

		startResize: function(e, direction) {
			var self = this
			var startX = e.clientX
			var img = this.$refs.contentEl ? this.$refs.contentEl.querySelector('img') : null
			if (!img) return
			var startWidth = img.offsetWidth

			function onMouseMove(ev) {
				var deltaX = ev.clientX - startX
				var newWidth
				if (direction === 'e' || direction === 'se') {
					newWidth = startWidth + deltaX
				} else {
					newWidth = startWidth - deltaX
				}
				newWidth = Math.max(40, Math.min(600, Math.round(newWidth)))
				img.style.width = newWidth + 'px'
				img.setAttribute('width', newWidth)
			}

			function onMouseUp() {
				document.removeEventListener('mousemove', onMouseMove)
				document.removeEventListener('mouseup', onMouseUp)
				// La larghezza finale passa dai settings: il content lo rigenera il renderer
				var finalWidth = img.getAttribute('width') || img.offsetWidth
				var settings = Object.assign({}, self.block.settings || {}, { width: String(finalWidth) })
				self.$emit('settings-change', { id: self.block.id, settings: settings })
			}

			document.addEventListener('mousemove', onMouseMove)
			document.addEventListener('mouseup', onMouseUp)
		},

		deleteChild(col, index) {
			var childId = col.children[index].id
			col.children.splice(index, 1)
			if (this.selectedBlockId === childId) {
				this.$emit('select', this.block.id)
			}
			this.$emit('child-change')
		}
	}
}
</script>

<style scoped>
.email-block {
	position: relative;
	cursor: pointer;
	transition: outline 0.15s;
	outline: 2px solid transparent;
	outline-offset: -2px;
}

/* z-index: il contorno di hover/selezione sporge sui vicini e va disegnato sopra di loro,
   altrimenti il blocco successivo copre il bordo inferiore con la propria riga */
.email-block--hover {
	outline: 2px dashed #64b5f6;
	z-index: 1;
}

.email-block--selected {
	outline: 3px solid #1976d2;
	z-index: 2;
}

/* Etichetta e barra stanno SOPRA il blocco, non dentro: appoggiate al bordo interno
   coprivano il contenuto — su un blocco a colonne mangiavano la prima riga della colonna
   sinistra e l'angolo in alto a destra di quella destra, cioe' proprio dove si rilascia */
.email-block__badge {
	position: absolute;
	top: -23px;
	left: 0;
	background: #1976d2;
	color: #fff;
	font-size: 12px;
	font-weight: 500;
	padding: 3px 10px;
	border-radius: 4px 4px 0 0;
	z-index: 3;
	line-height: 18px;
	pointer-events: none;
}

.email-block__toolbar {
	position: absolute;
	top: -25px;
	right: 0;
	display: flex;
	gap: 2px;
	z-index: 3;
	background: #1976d2;
	border-radius: 4px 4px 0 0;
	padding: 2px;
}

.email-block__tool {
	width: 30px;
	height: 28px;
	border: 0;
	border-radius: 3px;
	background: transparent;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
}

.email-block__tool .v-icon {
	color: #fff !important;
}

.email-block__tool:hover {
	background: rgba(255, 255, 255, 0.22);
}

.email-block__tool:disabled {
	cursor: default;
	opacity: 0.35;
}

.email-block__tool--ai {
	background: #fff;
	margin-right: 4px;
}

.email-block__tool--ai .v-icon {
	color: #1976d2 !important;
}

.email-block__tool--ai:hover {
	background: #e3f2fd;
}

.email-block__tool--danger:hover {
	background: #e53935;
}

/* Elementi modificabili sul posto (data-field): evidenti quando il blocco e' selezionato */
.email-block--selected .email-block__content >>> [data-field] {
	outline: 2px dashed #fb8c00;
	outline-offset: 3px;
	cursor: text;
}

.email-block--selected .email-block__content >>> [data-field]:hover {
	background: rgba(255, 152, 0, 0.1);
}

.email-block__content >>> .email-block__field--editing {
	outline: 2px solid #ff9800 !important;
	outline-offset: 2px;
	background: rgba(255, 152, 0, 0.06);
}

.email-block__content-wrapper {
	position: relative;
}

/* flow-root: il margine del primo titolo resta dentro il blocco. Senza, esce sopra e lascia vedere
   il bianco del canvas fra due blocchi dello stesso colore; nella mail il content sta in una td,
   dove il margine non esce, e la striscia non c'e' */
.email-block__content {
	min-height: 20px;
	display: flow-root;
}

.email-block__content[contenteditable="true"] {
	outline: 1px dashed #ff9800;
	outline-offset: -1px;
	cursor: text;
}

/* Inline text toolbar */
.email-block__text-toolbar {
	position: absolute;
	bottom: 100%;
	left: 0;
	display: flex;
	align-items: center;
	background: #fff;
	border: 1px solid #d0d0d0;
	border-bottom: none;
	border-radius: 6px 6px 0 0;
	padding: 2px 6px;
	z-index: 10;
	box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

.email-block__tb-btn {
	width: 32px !important;
	height: 32px !important;
	color: #555 !important;
}

.email-block__tb-btn:hover {
	color: #1976d2 !important;
	background: #f5f5f5 !important;
}

.email-block__tb-active {
	background: #e3f2fd !important;
	color: #1976d2 !important;
}

.email-block__tb-var-btn {
	text-transform: none !important;
	letter-spacing: 0 !important;
	font-size: 12px !important;
	color: #555 !important;
	height: 32px !important;
}

/* Link bar inline */
.email-block__link-bar {
	position: absolute;
	bottom: 100%;
	left: 0;
	display: flex;
	align-items: center;
	white-space: nowrap;
	background: #fff;
	border: 1px solid #d0d0d0;
	padding: 4px 6px;
	z-index: 20;
	margin-bottom: -1px;
	transform: translateY(-36px);
	box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

.email-block__link-field {
	width: 200px;
	min-width: 120px;
	border: 1px solid #ccc;
	border-radius: 3px;
	padding: 3px 6px;
	font-size: 12px;
	outline: none;
	font-family: inherit;
	background: #fff;
}

.email-block__link-field:focus {
	border-color: #1976d2;
}

.email-block__link-action {
	text-transform: none !important;
	letter-spacing: 0 !important;
	font-size: 11px !important;
	height: 24px !important;
	min-width: 0 !important;
	padding: 0 6px !important;
	margin-left: 4px !important;
}

/* Image resize handles */
.email-block__resize {
	position: absolute;
	background: #1976d2;
	border: 2px solid #fff;
	border-radius: 2px;
	z-index: 5;
}

.email-block__resize--e {
	right: -4px;
	top: 50%;
	transform: translateY(-50%);
	width: 8px;
	height: 24px;
	cursor: e-resize;
}

.email-block__resize--w {
	left: -4px;
	top: 50%;
	transform: translateY(-50%);
	width: 8px;
	height: 24px;
	cursor: w-resize;
}

.email-block__resize--se {
	right: -4px;
	bottom: -4px;
	width: 10px;
	height: 10px;
	cursor: se-resize;
	border-radius: 2px;
}

.email-block__resize--sw {
	left: -4px;
	bottom: -4px;
	width: 10px;
	height: 10px;
	cursor: sw-resize;
	border-radius: 2px;
}

.email-block__link-close {
	width: 22px !important;
	height: 22px !important;
	margin-left: 2px !important;
}

/* Layout columns */
.email-block__columns {
	display: flex;
	width: 100%;
}

.email-block__column {
	border: 1px dashed transparent;
	transition: border-color 0.15s;
	position: relative;
}

.email-block--hover > .email-block__columns > .email-block__column,
.email-block--selected > .email-block__columns > .email-block__column {
	border-color: #e0e0e0;
}

.email-block__column-drop {
	min-height: 60px;
}

.email-block__column-placeholder {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 60px;
	opacity: 0.5;
	pointer-events: none;
}

/* Show borders mode: confine di ogni sezione ben visibile, niente rumore sulle celle interne */
.email-block--show-borders {
	outline: 1px solid #9e9e9e;
	outline-offset: -1px;
}

.email-block--show-borders.email-block--hover {
	outline: 2px dashed #64b5f6;
}

.email-block--show-borders.email-block--selected {
	outline: 3px solid #1976d2;
}

.email-block--show-borders > .email-block__columns > .email-block__column {
	border-color: #b0b0b0;
	border-style: dashed;
}

.email-block__ghost {
	opacity: 0.4;
	background: #e3f2fd;
	outline: 2px dashed #1976d2;
}
</style>
